/* eslint-disable max-lines */
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from './../../../../common/models/content-heading';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AccountAdminCompanyCreateService } from 'private/app/services/account-admin/account-admin-company-create.service';
import { CompanyQueryService } from 'private/app/services/account-admin/company-query.service';
import { IdToken } from 'common/models';
import { OktaService } from 'common/services/okta/okta.service';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';
import { ToastService } from 'common/services/toast.service';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Option } from '../../../../common/components/select/select.component';
import { AppConstants } from 'common/app-constants';
import { UserType } from 'common/models/brand.model';
import { AccountAdminDetailsEditConfiguration, AccountAdminEditFormConfig, CompanyCategoryType } from '../account-admin-company-details/company-query/company-query-configuration';
import { ServicesType } from './business-information/business-information.component';
import { UntypedFormGroup } from '@angular/forms';
import { AddressLookup } from 'private/app/components/address-lookup/address-lookup.component';
import { environment } from 'common/environments/environment';
import { AccountAdminService } from 'private/app/services/account-admin/account-admin.service';
import { AccountStatusService } from 'common/services/account-status.service';
import { AccountStatusResponse, BaseResponse } from 'common/models/account-status';
import { BaseComponent } from 'common/components/base/base.component';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { DuplicateCompanyLookup } from 'private/app/components/duplicate-company-check/duplicate-company-check.component';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { CompanyRelationshipsResult } from 'private/app/models/account-admin-company.model';
import { DistributorService } from 'private/app/services/distributor-relationships.service';
import { AccountAdminExtendedService } from 'private/app/services/account-admin/account-admin-extended.service';
import { AccountAdminBrandsItemsEntity } from 'private/app/models/account-admin-search';

@Component({
    selector: 'hvac-create-company',
    templateUrl: './create-company.component.html',
    styleUrls: ['./create-company.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CreateCompanyComponent extends BaseComponent implements OnInit {
    public onSubmit$: Subject<void> = new Subject<void>();
    public authorisedBrandList$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public toastOutlet = 'createCompanyToast';
    public googleAddressValidationFeatureFlag = environment.features.googleAddressValidation;
    public companyCreateForm = this.companyCreateService.companyCreateForm;
    public breadCrumbData: ContentHeadingData;
    public userIsInternal: boolean;
    public userBrandFamily: string;
    public isCreate: boolean = false;
    public companyConfiguration: AccountAdminDetailsEditConfiguration;
    public initializeCreateCompanyAfterDuplicateCheck$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public apiRequestActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public addCompanyRelationship$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public relationshipFormReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public showAddressVerification$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public showDuplicateCompanyCheck$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public addressLookUpInput: AddressLookup;
    public duplicateCompanyCheckInput: DuplicateCompanyLookup;
    public modalToastStatus: boolean = false;
    public existingCompanyId: string = '';
    public existingCompanyName: string | undefined;
    public showModalFrMkngCrntCmpnyToAsPrmry: boolean = false;
    public existingPrimaryDistributorDetails: string = '';
    public isConfirmationModalVisible: boolean = false;
    public loading: boolean;
    public isServicesInvalid: boolean = false;
    public isInternalSuperAdmin: boolean;
    public isCompanyCategoryTypeDomDist: boolean;
    private isICPAdmin: boolean | undefined = false;
    private isMIDEAAdmin: boolean | undefined = false;
    private isFormReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private bannerType = 'CompanyCreate';
    private idmRoles: string[];
    private countriesAllowedToAddressCorrection = ['US', 'CA'];
    private isAddressVerificationRequired: boolean = false;
    private isDupliicateCompanyCheckRequired: boolean = false;
    private isSoldtoPrimaryExists: boolean = false;
    private isSoldToValidationComplete: boolean = false;

    constructor(
        private companyQuery: CompanyQueryService,
        public companyCreateService: AccountAdminCompanyCreateService,
        private oktaService: OktaService,
        public translate: TranslateService,
        public userAdminService: UserAdminService,
        public adminService: AccountAdminService,
        public accountAdminExtendedService: AccountAdminExtendedService,
        private readonly accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService,
        private toastService: ToastService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly statusService: AccountStatusService,
        protected distributorService: DistributorService
    ) {
        super();
        this.breadCrumbData = this.companyCreateService.breadCrumbData;
    }

    get shouldShowContinue(): boolean {
        const selectedCountry = this.companyCreateForm.controls.country.value?.[0]?.value;
        const isUSOrCanada = this.countriesAllowedToAddressCorrection.includes(selectedCountry);
        const needsSoldToValidation = this.companyCreateService.isCompanyCategoryDistributorType(
            this.companyCreateService.designation$.value.companyCategory
        ) && this.companyCreateForm.get('soldTo')?.value && !this.isSoldToValidationComplete;

        return isUSOrCanada || this.addCompanyRelationship$.value || needsSoldToValidation;
    }

    ngOnInit(): void {
        this.statusService.getUserStatus().pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((result: AccountStatusResponse) => {
            this.userIsInternal = (result.company.companyCategory.name === UserType.INTERNAL);
            this.userBrandFamily = result.brandFamily;
        });
        this.accountAdminExtendedService.getBrands(false).pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((brandList) => {
            const availableBrandList: Option[] = brandList.map((option:AccountAdminBrandsItemsEntity): Option => ({
                name: option.name,
                value: option.code
            })).slice().sort((valueA, valueB) => valueA.name.toLowerCase().localeCompare(valueB.name.toLowerCase()));
            this.authorisedBrandList$.next(availableBrandList);
        });
        this.companyCreateService.resetDesignation();
        this.toastService.removeAllTimeOuts();
        this.oktaService.idToken$.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((res: IdToken) => {
            this.companyCreateService.idmRoles$.next(this.accountAdminCompanyUtilsService.idmUserRole(res?.claims?.idm_user_roles || []));
            this.idmRoles = res?.claims?.idm_user_roles || [];
            this.isICPAdmin = [IdmUserRoles.COMPANYADMIN].every((role) => res?.claims?.idm_user_roles?.includes(role));
            this.isMIDEAAdmin = [IdmUserRoles.COMPANYADMIN, IdmUserRoles.MIDEA_BRAND_NA, IdmUserRoles.MIDEA_BRAND].every((role) => res?.claims?.idm_user_roles?.includes(role));
            this.isInternalSuperAdmin = this.idmRoles.includes(IdmUserRoles.INTERNAL_SUPER_ADMIN);
        });
        this.companyCreateService.designation$.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((designation) => {
            this.isAddressVerificationRequired = true;
            this.companyCreateService.initializeCompany();
            this.companyQuery.generateCompanyDetailsConfiguration(designation.companyCategory, designation.companyType, this.idmRoles);
            this.accountAdminCompanyUtilsService.updateComapnyCreateByForm(this.companyCreateForm, this.companyCreateService.company);

            this.accountAdminCompanyUtilsService.resetForm(this.companyCreateForm);
            if (designation.companyCategory && designation.companyType) {
                const companyCategory = this.companyCreateService.companyCategory.filter((category) => category.name === designation.companyCategory);
                const companyCategoryType = this.companyCreateService.companyCategory.filter((category) => category.name.toLowerCase() === designation.companyCategory.toLowerCase())[0]?.companyTypes?.filter((type) => type.code === designation.companyType);
                this.companyCreateService.company.companyCategory = {
                    name: companyCategory.length ? companyCategory[0].name : '',
                    code: companyCategory.length ? companyCategory[0].code : ''
                };

                this.companyCreateService.company.companyType = {
                    name: companyCategoryType?.length ? companyCategoryType[0].name : '',
                    code: companyCategoryType?.length ? `Z${companyCategoryType[0].code}` : ''
                };
                this.companyCreateService.checkValidation(this.companyCreateService.company.companyCategory.name, this.companyCreateService.company.companyType.name);
            }
        });

        this.companyCreateForm.controls.soldTo?.valueChanges.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe(() => {
            this.isSoldToValidationComplete = false;
            this.isSoldtoPrimaryExists = false;
        });

        this.companyQuery.companyDetailsConfiguration.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((configuration) => {
            this.companyConfiguration = configuration;
            this.addCompanyRelationship$.next(
                Boolean(configuration.configType === CompanyCategoryType.HVAC_CUSTOMER
                    || configuration.configType === CompanyCategoryType.INTERNATIONAL_CUSTOMER
                    || configuration.configType === CompanyCategoryType.MARINE_CUSTOMER
                    || configuration.configType === CompanyCategoryType.NATIONAL_ACCOUNT_CUSTOMER
                    || configuration.configType === CompanyCategoryType.VENDOR)
            );
        });

        this.companyCreateForm.controls.services?.valueChanges.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe(() => {
            this.isServicesInvalid = false;
            const services = this.companyCreateForm.controls.services?.value;

            if (!services || !this.companyConfiguration) {
                return;
            }

            const isDistributor = this.companyConfiguration.configType === CompanyCategoryType.DOMESTIC_DISTRIBUTOR || this.companyConfiguration.configType === CompanyCategoryType.INTERNATIONAL_DISTRIBUTOR;

            if (isDistributor && services.length === 1 && services[0].name === ServicesType.WarrantyDistributor) {
                this.isServicesInvalid = true;
            }
        });

        combineLatest([this.isFormReady, this.companyCreateForm.controls.country.valueChanges]).pipe(
            takeUntil(this.ngOnDestroy$)
        )
            .subscribe(([isFormReady, country]) => {
                if (this.apiRequestActive$.value) {
                    return;
                }
                if (isFormReady) {
                    if (this.isSoldtoPrimaryExists) {
                        return;
                    }

                    if (this.countriesAllowedToAddressCorrection.includes(country[0].value) && this.isAddressVerificationRequired && this.googleAddressValidationFeatureFlag) {
                        this.addressLookUpInput = this.companyAddressToBeValidate(this.companyCreateForm);
                        this.showAddressVerification$.next(true);
                        this.isFormReady.next(false);

                        return;
                    }

                    if (this.addCompanyRelationship$.value) {
                        this.adminService.isModalVisible = true;
                        this.isFormReady.next(false);

                        return;
                    }
                    this.processCreateCompany();
                }
            });

        combineLatest([
            this.companyCreateForm.controls.address1.valueChanges,
            this.companyCreateForm.controls.address2.valueChanges,
            this.companyCreateForm.controls.state.valueChanges,
            this.companyCreateForm.controls.country.valueChanges,
            this.companyCreateForm.controls.city.valueChanges,
            this.companyCreateForm.controls.postalCode.valueChanges
        ]).pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe(() => {
            if (!this.isAddressVerificationRequired) {
                this.isAddressVerificationRequired = true;
            }
        });
    }

    companyAddressToBeValidate(companyCreateForm: UntypedFormGroup): AddressLookup {
        return this.companyCreateService.companyAddressToValidate(companyCreateForm);
    }

    duplicateCompanyToBeValidate(companyCreateForm: UntypedFormGroup): DuplicateCompanyLookup {
        return this.companyCreateService.duplicateCompanyToBeValidate(companyCreateForm);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateOrAddRelationships($event: any) {
        if (!this.existingCompanyId) {
            this.updateRelationships($event);

            return;
        }

        if (this.existingCompanyId) {
            this.addRelationshipToExistingCompany($event);

            return;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateRelationships($event: any) {
        this.adminService.isModalVisible = false;
        this.accountAdminCompanyUtilsService.updateRelationshipToCompany($event, this.companyCreateService.company);
        this.processCreateCompany();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addRelationshipToExistingCompany($event: any) {
        this.adminService.isModalVisible = false;
        this.loading = true;
        const body: CompanyRelationshipsResult = {
            brandedRelationships: $event.brandedRelationships,
            pricingWarrantyRelationships: $event.pricingWarrantyRelationships,
            dealerCompanyId: Number(this.existingCompanyId),
            edit: $event.edit || false,
            deactivatedRelationships: []
        };
        let contentValue: string;
        this.distributorService.postAssociationsData(body).pipe(takeUntil(this.ngOnDestroy$)).subscribe((data:BaseResponse) => {
            this.loading = false;
            if (body.edit) {
                contentValue = data.details;
            }
            else if (body.deactivatedRelationships?.length === 0) {
                contentValue = `Company ${this.existingCompanyName} successfully updated.`;
            }
            else if (body.deactivatedRelationships?.length !== 0) {
                contentValue = this.translate.instant('ACCOUNT_ADMIN.DISTRIBUTOR_MODAL.RELATIONSHIP_REMOVED');
            }

            if (data.status === this.translate.instant('ACCOUNT_ADMIN.DISTRIBUTOR_MODAL.SUCCESS_MESSAGE')) {
                this.toastService.add({
                    content: contentValue,
                    id: this.translate.instant('ACCOUNT_ADMIN.DISTRIBUTOR_MODAL.RELATIONSHIP_SUCCESS'),
                    outletName: this.toastOutlet,
                    theme: 'success',
                    closeable: true,
                    autoClose: false
                });
                setTimeout(() => {
                    this.router.navigate(['/Admin/Admin-Tools/account-admin.html/company/', this.existingCompanyId], { relativeTo: this.route });

                    return;
                }, AppConstants.timeOutNavigation);
            }
        }, (error) => {
            this.loading = false;
            this.toastService.add({
                content: this.translate.instant(error.error.message),
                id: 'error',
                outletName: this.toastOutlet,
                theme: 'error',
                closeable: true,
                autoClose: true
            });
        });
    }

    initCreateCmpnyRqstIfValidInput() {
        this.userAdminService.smoothScrollToTop();
        const isFormInvalid = [];
        Object.keys(this.companyCreateForm.controls).forEach((key) => {
            this.companyCreateForm.get(key)?.markAsDirty();
            if (Object.prototype.hasOwnProperty.call(this.companyConfiguration.formConfig, key) && this.companyConfiguration.formConfig[key as keyof AccountAdminEditFormConfig]?.view && this.companyCreateForm.get(key)?.invalid) {
                isFormInvalid.push(key);
            }
        });

        const missingCoordinate = this.accountAdminCompanyUtilsService.missingCoordinate(this.companyCreateForm);
        if (isFormInvalid.length > 0 || missingCoordinate) {
            this.toastService.add({
                content: isFormInvalid.length > 0
                    ? this.translate.instant('ACCOUNT_ADMIN.COMPANY_CREATE.VALIDATION_ERROR')
                    : this.translate.instant('ACCOUNT_ADMIN.COMPANY_EDIT.COORDINATE_REQUIRED', { field: this.translate.instant(`ACCOUNT_ADMIN.${missingCoordinate}`) }),
                id: this.bannerType,
                outletName: this.toastOutlet,
                theme: 'error',
                closeable: true,
                autoClose: true
            });
        }
        else {
            this.validateSoldToPrimaryAccount();
            this.isFormReady.next(true);
            this.onSubmit$.next();
        }
    }

    onAddressConfirmation(address: AddressLookup | null): void {
        if (address === null) {
            this.showAddressVerification$.next(false);

            return;
        }

        this.companyCreateService.onAddressConfirmation(address);
        this.showAddressVerification$.next(false);
        this.isAddressVerificationRequired = false;
        this.isDupliicateCompanyCheckRequired = true;
        if (((this.isICPAdmin && this.userBrandFamily === 'ICP') || this.isMIDEAAdmin) && this.isDupliicateCompanyCheckRequired) {
            this.duplicateCompanyCheckInput = this.duplicateCompanyToBeValidate(this.companyCreateForm);
            this.duplicateCompanyCheckInput.brandFamily = this.userBrandFamily;
            this.showDuplicateCompanyCheck$.next(true);
            this.isFormReady.next(false);
        }
    }

    backToAccountAdmin() {
        if (this.companyCreateForm.dirty) {
            this.isConfirmationModalVisible = true;
        }
        else {
            this.router.navigate(
                ['/Admin/Admin-Tools/account-admin.html/'],
                { queryParamsHandling: 'merge' }
            );
        }
    }

    handleCloseModal() {
        if (this.isConfirmationModalVisible) {
            this.isConfirmationModalVisible = false;
        }
    }

    handleOkModal() {
        this.isConfirmationModalVisible = false;
        this.router.navigate(
            ['/Admin/Admin-Tools/account-admin.html/'],
            { queryParamsHandling: 'merge' }
        );
    }

    onDuplicateCompanyConfirmation(company: DuplicateCompanyLookup | null): void {
        if (company === null) {
            this.showDuplicateCompanyCheck$.next(false);

            return;
        }
        if (company.companyId) {
            this.isCreate = false;
            this.existingCompanyId = company.companyId;
            this.existingCompanyName = company.companyName;
        }

        if (!company.companyId) {
            this.isCreate = true;
        }

        this.companyCreateService.onDuplicateCompanyConfirmation(company);
        this.showDuplicateCompanyCheck$.next(false);
        this.isDupliicateCompanyCheckRequired = false;
        if (this.addCompanyRelationship$.value) {
            this.adminService.isModalVisible = true;

            return;
        }
    }

    toastOpenStatus(toastOpenStatus: boolean) {
        this.modalToastStatus = toastOpenStatus;
    }

    cancelForm() {
        this.router.navigate([`/Admin/Admin-Tools/account-admin.html`]);
    }

    initCreateCompany() {
        if (this.addCompanyRelationship$.value) {
            this.adminService.isModalVisible = true;

            return;
        }
        this.processCreateCompany();
    }

    cnclChngngExstingPrmryDstrbtr() {
        this.showModalFrMkngCrntCmpnyToAsPrmry = false;
        this.apiRequestActive$.next(false);
        this.isSoldtoPrimaryExists = false;
        this.isSoldToValidationComplete = false;
    }

    issueCreateCompanyPostRequest() {
        this.showModalFrMkngCrntCmpnyToAsPrmry = false;
        this.isSoldtoPrimaryExists = false;
        this.apiRequestActive$.next(false);
        this.isFormReady.next(true);
        this.onSubmit$.next();
    }

    private getUserCnsntFrMkngCrntSldToAsPrmry(primaryDistributorDetailsText = '') {
        const detailsText = typeof primaryDistributorDetailsText === 'string' ? primaryDistributorDetailsText.split('-') : [];
        this.existingPrimaryDistributorDetails = `${detailsText[1] || ''} - ${detailsText[2] || ''}`;
        this.showModalFrMkngCrntCmpnyToAsPrmry = true;
    }


    private validateSoldToPrimaryAccount() {
        if (
            this.companyCreateService.isCompanyCategoryDistributorType(this.companyCreateService.designation$.value.companyCategory) &&
            this.companyCreateService.company.isPrimaryAccount &&
            !this.isSoldToValidationComplete
        ) {
            this.apiRequestActive$.next(true);
            this.accountAdminCompanyUtilsService.updateComapnyCreateByForm(this.companyCreateForm, this.companyCreateService.company);
            this.companyQuery.isPrimaryDistributorExistsForCrntSoldToCmpny(this.companyCreateForm.get('soldTo')?.value)
                .subscribe((response: [boolean, string]) => {
                    const [isPrimaryDistributorAlreadyPresent, primaryDistributorDetailsText] = response;
                    this.isSoldToValidationComplete = true;
                    if (isPrimaryDistributorAlreadyPresent) {
                        this.isSoldtoPrimaryExists = true;
                        this.apiRequestActive$.next(false);
                        this.getUserCnsntFrMkngCrntSldToAsPrmry(primaryDistributorDetailsText);
                    }
                    else {
                        this.isSoldtoPrimaryExists = false;
                        this.apiRequestActive$.next(false);
                        this.isFormReady.next(true);
                        this.onSubmit$.next();
                    }
                });
        }
    }

    private processCreateCompany() {
        this.apiRequestActive$.next(true);
        this.toastService.remove(this.bannerType);
        this.accountAdminCompanyUtilsService.updateComapnyCreateByForm(this.companyCreateForm, this.companyCreateService.company);
        this.companyQuery.createCompany(this.companyCreateService.getCompanyData()).subscribe((companyCreateResponse) => {
            this.userAdminService.smoothScrollToTop();
            this.accountAdminCompanyUtilsService.resetForm(this.companyCreateForm);
            this.companyCreateForm.clearValidators();
            this.toastService.add({
                content: this.translate.instant('ACCOUNT_ADMIN.COMPANY_CREATE.COMPANY_CREATE_SUCCESS', {
                    companyName: companyCreateResponse.name,
                    companyId: companyCreateResponse.hvacCompanyId
                }),
                id: this.bannerType,
                outletName: this.toastOutlet,
                theme: 'success',
                closeable: true,
                autoClose: true
            });
            setTimeout(() => {
                this.companyCreateService.resetDesignation();
                this.router.navigate([`/Admin/Admin-Tools/account-admin.html/company/${companyCreateResponse.hvacCompanyId}`], { replaceUrl: true });

                return;
            }, AppConstants.timeOutNavigation);
        },
        (err) => {
            const toastContent = () => {
                if (err.error.message) {
                    return err.error.message;
                }
                else if (err.status === 400) {
                    return this.translate.instant('ACCOUNT_ADMIN.COMPANY_CREATE.VALIDATION_ERROR');
                }

                return err.error.details;
            };
            this.apiRequestActive$.next(false);
            this.toastService.add({
                content: toastContent(),
                id: this.bannerType,
                outletName: this.toastOutlet,
                theme: 'error',
                closeable: true,
                autoClose: true
            });
        });
    }
}
